/**
 * @stayai: This JS integrates Stay.ai bundle validation logic with the Monster Cart drawer.
 */

/**
 * Checks for unlinked bundle items in cart
 * @param {Array} lineItems - Cart items
 */
const checkOrphanBundleItems = async (lineItems) => {
  if (!lineItems || lineItems.length <= 0) return;

  let bundleItems = [...lineItems].map((item) => item?.properties?._bundle_id);
  let bundleIDs = [...new Set(bundleItems.filter((id) => id))];
  let orphanBundleIDs = [];

  if (!bundleIDs || bundleIDs.length <= 0) return;
  bundleIDs.forEach((bundleID) => {
    let bundleMin = 0;
    let bundleMax = 0;
    let bundleCount = 0;
    const hasChildProduct = lineItems.filter(
      (item) => item.properties?._bundle_id == bundleID && !item?.properties?._bundle_parent,
    );
    const hasParentProduct = lineItems.find(
      (item) => item.properties?._bundle_id == bundleID && item?.properties?._bundle_parent === true,
    );

    if (!hasChildProduct || hasChildProduct.length <= 0 || !hasParentProduct) {
      orphanBundleIDs.push(bundleID);
      return;
    }

    bundleMin = parseInt(hasParentProduct.properties?._bundle_min) || bundleMin;
    bundleMax = parseInt(hasParentProduct.properties?._bundle_max) || bundleMax;
    bundleCount = hasChildProduct.reduce((acc, item) => acc + parseInt(item.quantity), 0) || bundleCount;

    if (bundleCount < bundleMin || bundleCount > bundleMax) {
      orphanBundleIDs.push(bundleID);
    }
  });
  if (orphanBundleIDs.length <= 0) return;

  await deleteOrphanBundleProducts(lineItems, orphanBundleIDs);
};
/**
 * Removes all unlinked bundle products (one line per request for reliability).
 * @param {Array} cart - Cart line items
 * @param {Array} orphanBundleIDs - Array of unlinked products to delete
 */
const deleteOrphanBundleProducts = async (cart, orphanBundleIDs) => {
  const keysToRemove = [];
  orphanBundleIDs.forEach((bundleID) => {
    cart.forEach((item, index) => {
      if (!item || item?.properties?._bundle_id !== bundleID) return;
      const key = item.key;
      if (key && String(key).includes(":")) {
        keysToRemove.push(String(key));
      }
    });
  });

  /**
   * Duplicate keys to remove
    Because sometimes a bundle ID was in orphanBundleIDs twice, the inner loop over the cart ran twice for the same ID. The same line keys were added twice to keysToRemove, so we tried to remove the same lines twice. Sending an already-removed key to /cart/update.js can lead to wrong behavior (e.g. other lines or the whole cart being affected).
   *
   */
  const uniqueKeysToRemove = [...new Set(keysToRemove)];
  if (uniqueKeysToRemove.length === 0) return;

  try {
    await removeOrphanLines(uniqueKeysToRemove);
    window.__stayaiOrphanCheckCooldownUntil = Date.now() + 3000;
    const { Rebuy } = window;
    if (!Rebuy) {
      window.location.reload();
    }
  } catch (error) {
    console.error("Error removing bundle product from cart:", error);
  }
};

// Update one line per request and wait for each response before sending the next. This avoids the batch issues (key changes, wrong line updated, “removes all”).
async function removeOrphanLines(keysToRemove) {
  for (const key of keysToRemove) {
    const response = await fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ updates: { [key]: 0 } }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      console.warn(">>> cart/update.js non-OK for key", key, response.status, data);
      continue;
    }
  }
}

// @stayai: check for orphan Stay AI bundle items in monster cart (use fresh cart to avoid 422 from stale keys)
if (!window.__stayaiMonsterCartCartUpdatedRegistered) {
  let orphanCheckTimeout = null;
  window.__stayaiMonsterCartCartUpdatedRegistered = true;
  document.addEventListener("cart:updated", () => {
    if (!document.querySelector("#monster-upsell-cart")) return;
    if (window.__stayaiOrphanCheckInProgress) return;
    clearTimeout(orphanCheckTimeout);
    orphanCheckTimeout = setTimeout(async () => {
      if (window.__stayaiOrphanCheckInProgress) return;
      if (window.__stayaiOrphanCheckCooldownUntil && Date.now() < window.__stayaiOrphanCheckCooldownUntil) return;
      window.__stayaiOrphanCheckInProgress = true;
      try {
        let freshCart;
        try {
          const res = await fetch("/cart.js", { credentials: "same-origin" });
          freshCart = await res.json();
        } catch (err) {
          console.error(">>> fetch cart.js failed", err);
          return;
        }
        const items = freshCart?.items;
        if (!items || !items.length) return;
        await checkOrphanBundleItems(items);
      } finally {
        window.__stayaiOrphanCheckInProgress = false;
      }
    }, 50);
  });
}

